import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import classes from "./header.module.scss";
import assistantClasses from "../components/assistant/assistant.module.scss";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import Notifications from "./Notifications";
import UnratedMissionsBadge from "../components/Rating/UnratedMissionsBadge";
import Avatar from "./Avatar";
import UserProfileList from "./UserProfileList";
import NewMenu from "./NewMenu";
import QuickSearchDropdown from "./QuickSearchDropdown";
import { ReactComponent as CrxLogo } from "../assets/images/crx_header.svg";
import { useNotifications } from "../hooks/useNotifications";
import useCustomNavigate from "../hooks/useCustomNavigate";
import { filterNavByPermissions } from "../utils/navPermissions";
import { openCommandPalette } from "../utils/shortcuts";
import { findActiveNav, isChildPage, menuItems } from "./navMenu";
import { PageTitleContext } from "./pageTitleContext";
import AssistantDrawer from "../components/assistant/AssistantDrawer";
import DraftHost from "../components/assistant/DraftHost";
import PriceListStart from "../components/priceList/PriceListStart";
import AskHint, { useAskHint } from "../components/assistant/AskHint";
import AskWaitingButton from "../components/assistant/AskWaitingButton";
import AiWorking from "../components/assistant/AiWorking";
import LoadingLine from "./LoadingLine";
import { useAiAccess } from "../hooks/useAiAccess";
import { useAskWaiting } from "../hooks/useAskWaiting";

interface HeaderProps {
  isCollapsed: boolean;
  isMobile: boolean;
  onMobileMenuClick: () => void;
}

interface Crumb {
  label: string;
  to?: string;
}

/**
 * App header: brand (aligned with the sidebar), breadcrumb, search, the
 * "+ New" menu, rating reminder, notifications and the account menu.
 * Persists across navigation — it is part of the layout route.
 */
const Header: React.FC<HeaderProps> = ({ isCollapsed, isMobile, onMobileMenuClick }) => {
  const navigate = useNavigate();
  const customNavigate = useCustomNavigate();
  const location = useLocation();
  const { setCurrentUser, currentUser } = useUserPermissions();
  const { pageTitle } = useContext(PageTitleContext);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const {
    notifications,
    unreadCount,
    isLoading: notificationsLoading,
    hasMore: notificationsHasMore,
    loadMore: notificationsLoadMore,
    markAsRead,
    markAllAsRead,
    dismiss: dismissNotification,
  } = useNotifications();
  const { access: aiAccess } = useAiAccess();
  const notificationsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  // A dropdown that navigated somewhere — Profile, a notification row — closes.
  useEffect(() => {
    setShowNotifications(false);
    setShowUserProfile(false);
  }, [location.pathname]);

  // Clicking outside a header dropdown closes it.
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowUserProfile(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Logout in another tab logs this one out too.
  useEffect(() => {
    const logoutChannel = new BroadcastChannel("logout-channel");
    const handleLogoutMessage = (event: MessageEvent) => {
      if (event.data.type === "logout") {
        setCurrentUser(null);
        navigate("/login");
      }
    };
    logoutChannel.addEventListener("message", handleLogoutMessage);
    return () => logoutChannel.removeEventListener("message", handleLogoutMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const crumbs: Crumb[] = useMemo(() => {
    const nav = filterNavByPermissions(menuItems, currentUser?.pages || []);
    const active = findActiveNav(nav, location.pathname, location.search);
    if (active?.personal) {
      return [{ label: active.personal.label }];
    }
    if (active?.category && !active.item) {
      return [{ label: active.category.category }];
    }
    if (active?.category && active.item) {
      const overview = active.category.items.find((item) => item.shortLabel === "Overview");
      const isOverview = active.item === overview;
      const isListPage = location.pathname === active.item.to;
      // A page with no menu entry of its own (e.g. /finance/currency-rates) only
      // matched its group's Overview by prefix: name the page itself, not "Overview".
      if (isOverview && !isListPage && pageTitle) {
        return [
          { label: active.category.category, to: active.item.to },
          { label: pageTitle },
        ];
      }
      // A page reached from a menu page (Scheduled jobs → Queues): parent, then itself.
      if (isChildPage(location.pathname) && pageTitle) {
        return [
          { label: active.category.category, to: overview && !isOverview ? overview.to : undefined },
          { label: active.item.shortLabel ?? active.item.label, to: active.item.to },
          { label: pageTitle },
        ];
      }
      return [
        { label: active.category.category, to: overview && !isOverview ? overview.to : undefined },
        // On a record page the second crumb links back to its list.
        { label: active.item.shortLabel ?? active.item.label, to: isListPage ? undefined : active.item.to },
      ];
    }
    return pageTitle ? [{ label: pageTitle }] : [];
  }, [currentUser, location.pathname, location.search, pageTitle]);

  const handleCrumbClick = (event: React.MouseEvent, to: string) => {
    event.preventDefault();
    customNavigate(to);
  };

  const handleNotificationsClick = () => {
    setShowNotifications((prev) => !prev);
    setShowUserProfile(false);
  };

  const handleUserProfileClick = () => {
    setShowUserProfile((prev) => !prev);
    setShowNotifications(false);
  };

  const firstName = currentUser ? currentUser.firstName : "";
  const lastName = currentUser ? currentUser.lastName : "";
  const photoFile = currentUser ? currentUser.photoFile : null;

  const bell = (
    <div ref={notificationsRef} className={classes.dropdownAnchor}>
      <button
        type="button"
        className={`btn-unstyled ${classes.iconButton} ${showNotifications ? classes.iconButtonOn : ""}`}
        onClick={handleNotificationsClick}
        aria-haspopup="dialog"
        aria-expanded={showNotifications}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        title="Notifications"
      >
        <i className={`bi ${showNotifications ? "bi-bell-fill" : "bi-bell"}`} aria-hidden="true" />
        {unreadCount > 0 && (
          <span className={classes.badge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>
      {showNotifications && (
        <Notifications
          notifications={notifications}
          unreadCount={unreadCount}
          isLoading={notificationsLoading}
          hasMore={notificationsHasMore}
          onClose={() => setShowNotifications(false)}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onDismiss={dismissNotification}
          onLoadMore={notificationsLoadMore}
        />
      )}
    </div>
  );

  const profile = (
    <div ref={profileRef} className={classes.dropdownAnchor}>
      <button
        type="button"
        className={`btn-unstyled ${classes.profileButton}`}
        onClick={handleUserProfileClick}
        aria-haspopup="menu"
        aria-expanded={showUserProfile}
        aria-label={`Account menu for ${firstName} ${lastName}`.trim()}
        title="Your account"
      >
        <Avatar firstName={firstName} lastName={lastName} photoFile={photoFile} size={isMobile ? 32 : 30} />
        {!isMobile && <span className={classes.profileName}>{firstName} {lastName}</span>}
        {!isMobile && <i className={`bi bi-chevron-down ${classes.profileChevron}`} aria-hidden="true" />}
      </button>
      {showUserProfile && (
        <UserProfileList
          firstName={firstName}
          lastName={lastName}
          photoFile={photoFile}
          jobPosition={currentUser ? currentUser.jobPosition : null}
          onClose={() => setShowUserProfile(false)}
        />
      )}
    </div>
  );

  // The assistant: only for roles with AI on (render nothing otherwise). It is
  // asked from search (the header box, Ctrl+K, the phone's search icon), so
  // the header carries only the drawer and a one-time tip.
  const canAsk = !!aiAccess?.available;
  const askHint = useAskHint(canAsk);
  // Ask waiting on an answer: "Ask CRX · 1 waiting" on desktop, a gold dot on the phone's search icon.
  const { waiting: askWaiting } = useAskWaiting();
  // The form hosts are always there: search "Do" rows (New task, Ask for leave,
  // Upload a price list) work without AI too.
  const assistant = <>{canAsk && aiAccess ? <AssistantDrawer access={aiAccess} /> : null}<DraftHost /><PriceListStart /></>;

  if (isMobile) {
    return (
      <header className={`${classes.header} ${classes.headerMobile}`}>
        <button type="button" className={`btn-unstyled ${classes.iconButton}`} onClick={onMobileMenuClick} aria-label="Open menu">
          <i className="bi bi-list" aria-hidden="true" />
        </button>
        <Link to="/dashboard" className={classes.mobileBrand} aria-label="CRX home" onClick={(event) => handleCrumbClick(event, "/dashboard")}>
          <CrxLogo className={classes.logo} aria-hidden="true" />
          <span className={classes.brandName}>CRX</span>
        </Link>
        <span className={classes.spacer} />
        <button type="button"
          className={`btn-unstyled ${classes.iconButton} ${canAsk ? classes.iconButtonAsk : ""} ${askHint.visible ? "step-spotlight" : ""}`}
          onClick={() => { askHint.dismiss(); openCommandPalette(); }}
          aria-label={canAsk ? `Search, jump to a page, or ask CRX${askWaiting ? " — Ask is waiting on an answer" : ""}` : "Search or jump to a page"}>
          <i className="bi bi-search" aria-hidden="true" />
          {canAsk && askWaiting && <span className={assistantClasses.waitingDot} aria-hidden="true" />}
        </button>
        {askHint.visible && <AskHint isMobile onDismiss={askHint.dismiss} />}
        <AiWorking isCompact />
        {assistant}
        {bell}
        {profile}
        <LoadingLine />
      </header>
    );
  }

  return (
    <header className={classes.header}>
      <Link
        to="/dashboard"
        className={`${classes.brand} ${isCollapsed ? classes.brandCollapsed : ""}`}
        aria-label="CRX home"
        title="Dashboard"
        onClick={(event) => handleCrumbClick(event, "/dashboard")}
      >
        <CrxLogo className={classes.logo} aria-hidden="true" />
        {!isCollapsed && (
          <span className={classes.brandText}>
            <span className={classes.brandName}>CRX</span>
            <span className={classes.brandSub}>Internal management</span>
          </span>
        )}
      </Link>

      <nav className={classes.crumbs} aria-label="Breadcrumb">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <React.Fragment key={`${crumb.label}-${index}`}>
              {index > 0 && <i className={`bi bi-chevron-right ${classes.crumbSep}`} aria-hidden="true" />}
              {crumb.to ? (
                <Link to={crumb.to} className={classes.crumbLink} onClick={(event) => handleCrumbClick(event, crumb.to as string)}>
                  <span className={isLast ? classes.crumbCurrentText : classes.crumbText}>{crumb.label}</span>
                </Link>
              ) : (
                <span className={isLast ? classes.crumbCurrentText : classes.crumbText} aria-current={isLast ? "page" : undefined}>
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      <div className={`${classes.searchSlot} ${classes.searchSlotAnchor}`} onFocus={askHint.visible ? askHint.dismiss : undefined}>
        <div className={askHint.visible ? "step-spotlight" : undefined}>
          <QuickSearchDropdown />
        </div>
        {askHint.visible && <AskHint onDismiss={askHint.dismiss} />}
      </div>

      <div className={classes.actions}>
        <AiWorking />
        {canAsk && <AskWaitingButton />}
        <NewMenu />
        <UnratedMissionsBadge />
        {assistant}
        {bell}
        {profile}
      </div>
      <LoadingLine />
    </header>
  );
};

export default Header;
